import moment from 'moment';
import { PRODUTO_BANANA_ID } from 'utils/variaveisGlobais';
import { toPrice } from 'utils/converters';

export const OPTIONS_TIPO_EMISSAO_CACHOS = [
  { id: 'semanal', descricao: 'Semanal' },
  { id: 'quinzenal', descricao: 'Quinzenal' },
];

export const MARCACAO_CACHOS_PAGE = 1;
export const MARCACAO_CACHOS_PAGE_SIZE = 100;
export const MARCACAO_CACHOS_PAGE_SIZE_OPTIONS = [100, 200, 300];

export const MARCACAO_CACHOS_MODAIS_DEFAULT = {
  update: false,
  remove: false,
  diasGrupo: false,
};

export function getDefaultMarcacaoFilters() {
  return {
    dateStart: moment().startOf('month').format('YYYY-MM-DD'),
    dateEnd: moment().endOf('month').format('YYYY-MM-DD'),
    talhaoId: undefined,
    codigoEmissao: undefined,
    order_by: 'data',
    order: 'DESC',
    page: MARCACAO_CACHOS_PAGE,
    pageSize: MARCACAO_CACHOS_PAGE_SIZE,
  };
}

function getMarcacaoScopeFilters(
  filters,
  { includeCodigoEmissao = true, includeDates = true } = {},
) {
  const scope = {};

  if (includeDates && filters?.dateStart && filters?.dateEnd) {
    scope.dataInicio = filters.dateStart;
    scope.dataFim = filters.dateEnd;
  }

  if (filters?.talhaoId) {
    scope.talhaoId = filters.talhaoId;
  }

  if (includeCodigoEmissao && filters?.codigoEmissao) {
    scope.codigoEmissao = filters.codigoEmissao;
  }

  return scope;
}

export function isFazendaAtivaReady(fazendaAtiva) {
  return fazendaAtiva?.id === 0 || Boolean(fazendaAtiva?.id);
}

export function getMarcacaoFazendaFilter(fazendaAtiva) {
  if (!isFazendaAtivaReady(fazendaAtiva)) return {};
  return { fazenda: fazendaAtiva.id };
}

export function buildMarcacaoListFilter(safraId, filters, fazendaAtiva) {
  return {
    safra: safraId,
    ...getMarcacaoFazendaFilter(fazendaAtiva),
    page: filters.page,
    pageSize: filters.pageSize,
    ...getMarcacaoScopeFilters(filters),
    ...(filters.order_by
      ? { order_by: filters.order_by, order: filters.order || 'DESC' }
      : {}),
  };
}

export function buildMarcacaoEmissoesFilter(safraId, filters, fazendaAtiva) {
  return {
    safra: safraId,
    ...getMarcacaoFazendaFilter(fazendaAtiva),
    ...getMarcacaoScopeFilters(filters, {
      includeCodigoEmissao: false,
      includeDates: false,
    }),
  };
}

export function getDefaultMarcacaoDashboardFilters() {
  return {
    dateStart: undefined,
    dateEnd: undefined,
    talhaoId: undefined,
    codigoEmissao: undefined,
  };
}

export function getTalhaoIdFromNavigation(location) {
  const fromState = location?.state?.talhaoId;
  const fromQuery = new URLSearchParams(location?.search || '').get('talhaoId');
  const raw = fromState ?? fromQuery;

  if (raw == null || raw === '') return undefined;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? raw : parsed;
}

export function getMarcacaoFiltersFromNavigation(location) {
  const filters = {};
  const talhaoId = getTalhaoIdFromNavigation(location);

  if (talhaoId != null) {
    filters.talhaoId = talhaoId;
  }

  const dateStart = location?.state?.dateStart;
  const dateEnd = location?.state?.dateEnd;

  if (dateStart && dateEnd) {
    filters.dateStart = dateStart;
    filters.dateEnd = dateEnd;
  }

  return filters;
}

export function buildMarcacaoNavigationWithTalhao(pathname, options) {
  const resolved =
    options != null && typeof options === 'object'
      ? options
      : { talhaoId: options };

  const { talhaoId, dateStart, dateEnd } = resolved;
  const normalized =
    talhaoId != null && talhaoId !== '' ? Number(talhaoId) : undefined;
  const hasTalhao =
    normalized != null && normalized !== '' && !Number.isNaN(normalized);
  const hasDateRange = Boolean(dateStart && dateEnd);
  const search = hasTalhao ? `?talhaoId=${normalized}` : '';

  const state = {};

  if (hasTalhao) {
    state.talhaoId = normalized;
  }

  if (hasDateRange) {
    state.dateStart = dateStart;
    state.dateEnd = dateEnd;
  }

  return {
    pathname,
    search,
    state: Object.keys(state).length ? state : undefined,
  };
}

export function buildMarcacaoDashboardFilter(safraId, filters, fazendaAtiva) {
  return {
    safra: safraId,
    ...getMarcacaoFazendaFilter(fazendaAtiva),
    ...getMarcacaoScopeFilters(filters),
  };
}

export function buildCortesSaldoFilter(safraId, filters, fazendaAtiva) {
  return {
    safra: safraId,
    ...getMarcacaoFazendaFilter(fazendaAtiva),
    ...getMarcacaoScopeFilters(filters, { includeCodigoEmissao: false }),
  };
}

export function getDefaultCortesCachosFilters() {
  return {
    dateStart: moment().startOf('month').format('YYYY-MM-DD'),
    dateEnd: moment().endOf('month').format('YYYY-MM-DD'),
    talhaoId: undefined,
    usuarioId: undefined,
  };
}

export function getCortesDatePresetRange(preset) {
  const day = preset === 'ontem' ? moment().subtract(1, 'day') : moment();
  const date = day.format('YYYY-MM-DD');

  return {
    dateStart: date,
    dateEnd: date,
  };
}

export function getActiveCortesDatePreset(filters) {
  if (!filters?.dateStart || !filters?.dateEnd) return null;
  if (filters.dateStart !== filters.dateEnd) return null;

  const hoje = moment().format('YYYY-MM-DD');
  const ontem = moment().subtract(1, 'day').format('YYYY-MM-DD');

  if (filters.dateStart === hoje) return 'hoje';
  if (filters.dateStart === ontem) return 'ontem';

  return null;
}

export function buildCortesConsultaFilter(safraId, filters, fazendaAtiva) {
  return {
    ...buildCortesSaldoFilter(safraId, filters, fazendaAtiva),
    page: MARCACAO_CACHOS_PAGE,
    pageSize: MARCACAO_CACHOS_PAGE_SIZE,
    escopoConta: 1,
  };
}

export function formatCorteHorario(horario) {
  if (!horario) return '—';

  const text = String(horario).trim();
  const match = text.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (match) {
    return `${match[1].padStart(2, '0')}:${match[2]}`;
  }

  const parsed = moment(text, ['HH:mm:ss', 'HH:mm'], true);
  return parsed.isValid() ? parsed.format('HH:mm') : text;
}

export function flattenViagensColheita(colheitas) {
  const items = (colheitas || []).flatMap((colheita) =>
    (colheita.viagens || []).map((viagem) => ({
      id: viagem.id ?? `${colheita.id}-${viagem.numeroViagem}`,
      colheitaId: colheita.id,
      data: colheita.data,
      horario: viagem.horario,
      numeroViagem: viagem.numeroViagem,
      quantidade: viagem.quantidade,
      talhaoNumero: colheita.talhaoNumero,
      talhaoNome: colheita.talhaoNome,
      usuarioId: colheita.usuarioId,
      usuarioNome: colheita.usuarioNome || '',
    })),
  );

  return items.sort((a, b) => {
    const dateA = moment(`${a.data} ${a.horario || '00:00:00'}`);
    const dateB = moment(`${b.data} ${b.horario || '00:00:00'}`);

    return (
      dateB.valueOf() - dateA.valueOf() ||
      Number(b.numeroViagem || 0) - Number(a.numeroViagem || 0)
    );
  });
}

export function normalizeCortesListContent(response) {
  if (Array.isArray(response)) return response;
  return response?.content || [];
}

export function countViagensColheita(colheitas) {
  return flattenViagensColheita(colheitas).length;
}

export function matchesCortesUsuarioFilter(item, usuarioId) {
  if (!usuarioId) return true;
  if (item?.usuarioId == null) return false;
  return String(item.usuarioId) === String(usuarioId);
}

export function filterCortesColheitasByUsuario(colheitas, usuarioId) {
  if (!usuarioId) return colheitas || [];
  return (colheitas || []).filter((item) =>
    matchesCortesUsuarioFilter(item, usuarioId),
  );
}

export function filterCortesDescartesByUsuario(descartes, usuarioId) {
  if (!usuarioId) return descartes || [];
  return (descartes || []).filter((item) =>
    matchesCortesUsuarioFilter(item, usuarioId),
  );
}

export function buildCortesPessoaFilterOptions(colheitas, descartes) {
  const map = new Map();

  const add = (item) => {
    if (item?.usuarioId == null) return;

    const id = item.usuarioId;
    const nome = String(item.usuarioNome || '').trim() || 'Não informado';

    if (!map.has(String(id))) {
      map.set(String(id), { id, nome });
    }
  };

  (colheitas || []).forEach(add);
  (descartes || []).forEach(add);

  return Array.from(map.values()).sort((a, b) =>
    a.nome.localeCompare(b.nome, 'pt-BR'),
  );
}

export function computeCortesConsultaResumo(colheitas, descartes) {
  const colheita = (colheitas || []).reduce(
    (acc, item) => acc + Number(item.totalQuantidade || 0),
    0,
  );
  const descarte = (descartes || []).reduce(
    (acc, item) => acc + Number(item.quantidade || 0),
    0,
  );

  const motivoMap = new Map();

  (descartes || []).forEach((item) => {
    const key = String(item.motivoId ?? item.motivoDescricao ?? 'sem-motivo');
    const atual = motivoMap.get(key) || {
      motivoId: item.motivoId,
      descricao: item.motivoDescricao || 'Sem motivo',
      quantidade: 0,
    };

    atual.quantidade += Number(item.quantidade || 0);
    motivoMap.set(key, atual);
  });

  return {
    cards: { colheita, descarte },
    porMotivo: Array.from(motivoMap.values()).sort(
      (a, b) => Number(b.quantidade || 0) - Number(a.quantidade || 0),
    ),
  };
}

export function sortColheitasConsulta(colheitas) {
  return [...(colheitas || [])].sort(
    (a, b) =>
      moment(b.data).valueOf() - moment(a.data).valueOf() ||
      Number(b.id || 0) - Number(a.id || 0),
  );
}

export function normalizeCorteData(data) {
  if (!data) return null;

  const parsed = moment(data);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

export function getCortesDescarteParaColheita(descartes, colheita) {
  const matches = (descartes || []).filter((descarte) => {
    if (colheita?.usuarioId == null || descarte?.usuarioId == null) {
      return false;
    }
    if (String(colheita.usuarioId) !== String(descarte.usuarioId)) {
      return false;
    }

    if (colheita.talhaoId != null && descarte.talhaoId != null) {
      if (Number(colheita.talhaoId) !== Number(descarte.talhaoId)) {
        return false;
      }
    }

    const dataColheita = normalizeCorteData(colheita.data);
    const dataDescarte = normalizeCorteData(descarte.data);

    if (dataColheita && dataDescarte && dataColheita !== dataDescarte) {
      return false;
    }

    return true;
  });

  return {
    quantidade: matches.reduce(
      (acc, item) => acc + Number(item.quantidade || 0),
      0,
    ),
    registros: matches.length,
  };
}

export function buildViagensPorPessoaResumo(colheitas, descartes) {
  const map = new Map();

  (colheitas || []).forEach((colheita) => {
    const data = normalizeCorteData(colheita.data) || '';
    const usuarioId = colheita.usuarioId ?? 'sem-usuario';
    const talhaoId = colheita.talhaoId ?? 'sem-talhao';
    const key = `${data}|${usuarioId}|${talhaoId}`;
    const usuarioNome =
      String(colheita.usuarioNome || '').trim() || 'Não informado';
    const talhaoNome = colheita.talhaoNome || '—';
    const talhaoNumero = colheita.talhaoNumero || '';

    if (!map.has(key)) {
      map.set(key, {
        id: key,
        data,
        usuarioId,
        usuarioNome,
        talhaoId,
        talhaoNome,
        talhaoNumero,
        totalViagens: 0,
        totalQuantidade: 0,
      });
    }

    const group = map.get(key);
    const viagens = colheita.viagens || [];

    if (viagens.length) {
      viagens.forEach((viagem) => {
        group.totalViagens += 1;
        group.totalQuantidade += Number(viagem.quantidade || 0);
      });
    } else {
      group.totalQuantidade += Number(colheita.totalQuantidade || 0);
    }
  });

  return Array.from(map.values())
    .map((group) => ({
      ...group,
      totalDescarte: getCortesDescarteParaColheita(descartes, {
        usuarioId: group.usuarioId,
        talhaoId: group.talhaoId,
        data: group.data,
      }).quantidade,
    }))
    .filter((item) => item.totalViagens > 0 || item.totalQuantidade > 0)
    .sort(
      (a, b) =>
        moment(b.data).valueOf() - moment(a.data).valueOf() ||
        a.usuarioNome.localeCompare(b.usuarioNome, 'pt-BR') ||
        a.talhaoNome.localeCompare(b.talhaoNome, 'pt-BR'),
    );
}

export function buildColheitaPorTalhaoResumo(colheitas, descartes) {
  const map = new Map();

  const ensureGroup = (talhaoId, talhaoNumero, talhaoNome) => {
    const key = String(talhaoId ?? 'sem-talhao');
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        talhaoId: talhaoId ?? 'sem-talhao',
        talhaoNumero: talhaoNumero || '—',
        talhaoNome: talhaoNome || '—',
        totalQuantidade: 0,
        totalViagens: 0,
        totalDescarte: 0,
      });
    }
    return map.get(key);
  };

  (colheitas || []).forEach((colheita) => {
    const group = ensureGroup(
      colheita.talhaoId,
      colheita.talhaoNumero,
      colheita.talhaoNome,
    );
    group.totalQuantidade += Number(colheita.totalQuantidade || 0);
    group.totalViagens += colheita.viagens?.length || 0;
  });

  (descartes || []).forEach((descarte) => {
    const group = ensureGroup(
      descarte.talhaoId,
      descarte.talhaoNumero,
      descarte.talhaoNome,
    );
    group.totalDescarte += Number(descarte.quantidade || 0);
  });

  return Array.from(map.values()).sort(
    (a, b) =>
      Number(b.totalQuantidade || 0) - Number(a.totalQuantidade || 0) ||
      a.talhaoNome.localeCompare(b.talhaoNome, 'pt-BR'),
  );
}

export function buildColheitaPorPessoaResumo(colheitas, descartes) {
  const map = new Map();

  const ensureGroup = (usuarioId, usuarioNome) => {
    const key = String(usuarioId ?? 'sem-usuario');
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        usuarioId: usuarioId ?? 'sem-usuario',
        usuarioNome: String(usuarioNome || '').trim() || 'Não informado',
        totalQuantidade: 0,
        totalViagens: 0,
        totalDescarte: 0,
        totalColheitas: 0,
      });
    }
    return map.get(key);
  };

  (colheitas || []).forEach((colheita) => {
    const group = ensureGroup(colheita.usuarioId, colheita.usuarioNome);
    group.totalQuantidade += Number(colheita.totalQuantidade || 0);
    group.totalViagens += colheita.viagens?.length || 0;
    group.totalColheitas += 1;
  });

  (descartes || []).forEach((descarte) => {
    const group = ensureGroup(descarte.usuarioId, descarte.usuarioNome);
    group.totalDescarte += Number(descarte.quantidade || 0);
  });

  return Array.from(map.values()).sort(
    (a, b) =>
      Number(b.totalQuantidade || 0) - Number(a.totalQuantidade || 0) ||
      a.usuarioNome.localeCompare(b.usuarioNome, 'pt-BR'),
  );
}

export function mapDonutMotivoData(porMotivo) {
  const items = porMotivo || [];
  const total = items.reduce(
    (acc, item) => acc + Number(item.quantidade || 0),
    0,
  );

  if (!total) {
    return { series: [], labels: [] };
  }

  return {
    series: items.map((item) => Number(item.quantidade || 0)),
    labels: items.map(
      (item) =>
        `${item.descricao || 'Sem motivo'} - ${Number(item.quantidade || 0)}`,
    ),
  };
}

export function mapSaldoPorTalhaoRanking(porTalhao) {
  const items = porTalhao || [];
  const totalEmissao = items.reduce(
    (acc, item) => acc + Number(item.marcados ?? item.emissao ?? 0),
    0,
  );

  return [...items]
    .map((item) => {
      const saldo = Number(item.saldo || 0);
      const emissao = Number(item.marcados ?? item.emissao ?? 0);
      const areaHa = Number(item.areaHa || 0);
      const quantidadePorHa = areaHa > 0 ? emissao / areaHa : null;
      const participacao =
        totalEmissao > 0 ? (emissao / totalEmissao) * 100 : 0;

      return {
        ...item,
        numero: item.numero || item.codigo || '',
        emissao,
        cortado: Number(item.colheita ?? 0),
        descartado: Number(item.descarte ?? 0),
        quantidade: saldo,
        quantidadePorHa,
        participacao,
      };
    })
    .sort((a, b) => Number(b.quantidade || 0) - Number(a.quantidade || 0));
}

export function sortSaldoPorTalhaoItems(items, sort = {}) {
  const orderBy = sort.order_by || 'quantidade';
  const order = sort.order || 'DESC';
  const direction = order === 'ASC' ? 1 : -1;

  return [...(items || [])].sort((linhaA, linhaB) => {
    const valueA = Number(linhaA?.[orderBy] ?? 0);
    const valueB = Number(linhaB?.[orderBy] ?? 0);
    const comparison = (valueA - valueB) * direction;

    if (comparison !== 0) return comparison;

    return String(linhaA.nome || '').localeCompare(
      String(linhaB.nome || ''),
      'pt-BR',
    );
  });
}

export function getSaldoPorTalhaoTotais(items) {
  return (items || []).reduce(
    (acc, item) => ({
      areaHa: acc.areaHa + Number(item.areaHa || 0),
      emissao: acc.emissao + Number(item.emissao || 0),
      cortado: acc.cortado + Number(item.cortado || 0),
      descartado: acc.descartado + Number(item.descartado || 0),
      saldo: acc.saldo + Number(item.quantidade || 0),
    }),
    { areaHa: 0, emissao: 0, cortado: 0, descartado: 0, saldo: 0 },
  );
}

export function mergeSaldoTalhaoComArea(porTalhaoSaldo, porTalhaoMarcacao) {
  const marcacaoByTalhao = new Map(
    (porTalhaoMarcacao || []).map((item) => [item.talhaoId, item]),
  );

  return (porTalhaoSaldo || []).map((item) => {
    const marcacao = marcacaoByTalhao.get(item.talhaoId);

    return {
      ...item,
      areaHa: item.areaHa ?? marcacao?.areaHa,
      marcados: item.marcados ?? marcacao?.quantidade,
    };
  });
}

export function buildMarcacaoTalhoesReducedFilter(
  safraId,
  fazendaAtiva,
  { filterByFazenda = true } = {},
) {
  if (!safraId) return null;

  if (filterByFazenda && !isFazendaAtivaReady(fazendaAtiva)) return null;

  if (!filterByFazenda) {
    return { safra: safraId };
  }

  return {
    safra: safraId,
    ...getMarcacaoFazendaFilter(fazendaAtiva),
  };
}

export function isTalhaoMarcacaoSelectivel(talhao) {
  return String(talhao?.nome || '').toUpperCase() !== 'GASTOS GERAIS';
}

export function filterTalhoesMarcacaoSelect(talhoes) {
  return (talhoes || []).filter(isTalhaoMarcacaoSelectivel);
}

export function getEmissaoMarcadosQuantidade(item) {
  if (item?.marcados != null) return Number(item.marcados || 0);
  return Number(item?.totalQuantidade || 0);
}

export function getEmissaoChartQuantidade(item) {
  if (item?.saldo != null) return Number(item.saldo || 0);
  return getEmissaoMarcadosQuantidade(item);
}

export function filterEmissoesComSaldoAberto(porEmissao) {
  return (porEmissao || []).filter((item) => Number(item.saldo || 0) > 0);
}

export const EMISSAO_CHART_COLORS = [
  '#368467',
  '#3b82f6',
  '#8b5cf6',
  '#5b9a7d',
  '#0d9488',
  '#6366f1',
  '#7ec9a8',
  '#60a5fa',
  '#a78bfa',
  '#2dd4bf',
];

export function getEmissaoChartColors(count) {
  const total = Math.max(Number(count) || 0, 0);
  if (!total) return [];

  return Array.from(
    { length: total },
    (_, index) => EMISSAO_CHART_COLORS[index % EMISSAO_CHART_COLORS.length],
  );
}

export function mapBarEmissaoData(porEmissao) {
  const items = porEmissao || [];

  return {
    categories: items.map((item) => item.codigo),
    data: items.map((item) => getEmissaoChartQuantidade(item)),
    colors: getEmissaoChartColors(items.length),
  };
}

export function mapDonutEmissaoData(porEmissao) {
  const items = porEmissao || [];
  const total = items.reduce(
    (acc, item) => acc + getEmissaoChartQuantidade(item),
    0,
  );

  if (!total) {
    return { series: [], labels: [], colors: [] };
  }

  return {
    series: items.map((item) => getEmissaoChartQuantidade(item)),
    labels: items.map(
      (item) => `${item.codigo} - ${getEmissaoChartQuantidade(item)}`,
    ),
    colors: getEmissaoChartColors(items.length),
  };
}

export function buildMarcacaoResumoFilter(filter) {
  if (!filter?.safra) return {};

  const {
    page: _page,
    pageSize: _pageSize,
    order_by: _orderBy,
    order: _order,
    ...scope
  } = filter;
  return scope;
}

export function hasMarcacaoDateFilter(filters) {
  return Boolean(filters?.dateStart && filters?.dateEnd);
}

export function hasMarcacaoEmissaoFilter(filters) {
  return Boolean(filters?.codigoEmissao);
}

export function getMarcacaoFiltroPeriodoEmissaoHint() {
  return 'Período e emissão não são usados juntos. Ao escolher um, o outro é limpo automaticamente.';
}

export function formatMarcacaoDateRangeLabel(
  filters,
  fallback = 'Período filtrado',
  separator = ' → ',
) {
  if (!hasMarcacaoDateFilter(filters)) {
    return fallback;
  }

  const inicio = moment(filters.dateStart).format('DD/MM/YYYY');
  const fim = moment(filters.dateEnd).format('DD/MM/YYYY');
  return `${inicio}${separator}${fim}`;
}

export function getMarcacaoTotalResumoLabel(filters) {
  if (hasMarcacaoEmissaoFilter(filters)) {
    return `Total na emissão ${filters.codigoEmissao}`;
  }

  if (hasMarcacaoDateFilter(filters)) {
    return `Total de ${formatMarcacaoDateRangeLabel(filters, '', ' a ')}`;
  }

  return 'Total filtrado';
}

export function mapEmissaoFilterOptions(emissoes) {
  return (emissoes || []).map((item) => ({
    id: item.codigo,
    descricao: `${item.codigo} (${item.totalQuantidade} cachos)`,
  }));
}

export function mergeMarcacaoDateFilterUpdate(
  old,
  next,
  { resetPage = false } = {},
) {
  const dateStart = next.dateStart || undefined;
  const dateEnd = next.dateEnd || undefined;
  const hasDateRange = Boolean(dateStart && dateEnd);

  const datesChanged =
    (old.dateStart || undefined) !== dateStart ||
    (old.dateEnd || undefined) !== dateEnd;

  if (!datesChanged) {
    return next;
  }

  return {
    ...next,
    dateStart,
    dateEnd,
    ...(hasDateRange ? { codigoEmissao: undefined } : {}),
    ...(resetPage ? { page: MARCACAO_CACHOS_PAGE } : {}),
  };
}

export function mergeMarcacaoEmissaoFilterUpdate(
  old,
  option,
  { resetPage = false, restoreListDates = false } = {},
) {
  if (option?.id) {
    return {
      ...old,
      codigoEmissao: option.id,
      dateStart: undefined,
      dateEnd: undefined,
      ...(resetPage ? { page: MARCACAO_CACHOS_PAGE } : {}),
    };
  }

  const defaults = restoreListDates
    ? getDefaultMarcacaoFilters()
    : { dateStart: undefined, dateEnd: undefined };

  return {
    ...old,
    codigoEmissao: undefined,
    dateStart: defaults.dateStart,
    dateEnd: defaults.dateEnd,
    ...(resetPage ? { page: MARCACAO_CACHOS_PAGE } : {}),
  };
}

export function formatTalhaoLabel(talhao) {
  if (!talhao) return '';
  if (talhao.numero) {
    return `${talhao.nome} (${talhao.numero})`;
  }
  return talhao.nome;
}

export function formatTalhaoCodigo(talhao) {
  if (!talhao?.numero) return '—';
  return talhao.numero;
}

export function formatTalhaoNome(talhao) {
  if (!talhao) return '-';
  return talhao.nome;
}

export function formatQuantidadePorHa(value) {
  if (value == null || value === '') return '—';
  return toPrice(value, 2, 2);
}

export function getMarcacaoGrupoChave(item) {
  const talhaoId = item?.talhao?.id ?? item?.talhaoId ?? 0;
  const codigoEmissao = String(item?.codigoEmissao || '').trim();
  return `${talhaoId}|${codigoEmissao}`;
}

export function groupMarcacoesPorTalhaoEmissao(content = []) {
  const gruposPorChave = new Map();
  const ordemChaves = [];

  content.forEach((item) => {
    const chave = getMarcacaoGrupoChave(item);
    if (!gruposPorChave.has(chave)) {
      gruposPorChave.set(chave, []);
      ordemChaves.push(chave);
    }
    gruposPorChave.get(chave).push(item);
  });

  return ordemChaves.map((chave) => {
    const itens = [...gruposPorChave.get(chave)].sort((anterior, atual) => {
      const dataAnterior = moment(anterior.data).valueOf();
      const dataAtual = moment(atual.data).valueOf();
      return dataAnterior - dataAtual || Number(anterior.id) - Number(atual.id);
    });

    const marcacao = itens[0];
    const adicionais = itens.slice(1).sort((anterior, atual) => {
      const dataAnterior = moment(anterior.data).valueOf();
      const dataAtual = moment(atual.data).valueOf();
      return dataAtual - dataAnterior || Number(atual.id) - Number(anterior.id);
    });
    const quantidadeTotal = itens.reduce(
      (total, item) => total + Number(item.quantidade || 0),
      0,
    );
    const areaHa = Number(marcacao?.talhao?.areaHa) || 0;

    return {
      chave,
      marcacao,
      adicionais,
      outrosCount: adicionais.length,
      quantidadeTotal,
      quantidadePorHaTotal: areaHa > 0 ? quantidadeTotal / areaHa : null,
    };
  });
}

export function getMelhorMenorRendimentoTalhao(porTalhao) {
  const eligible = (porTalhao || []).filter(
    (item) =>
      Number(item.areaHa) > 0 &&
      item.quantidadePorHa != null &&
      !Number.isNaN(Number(item.quantidadePorHa)),
  );

  if (!eligible.length) {
    return { melhor: null, menor: null };
  }

  const sorted = [...eligible].sort(
    (a, b) => Number(a.quantidadePorHa) - Number(b.quantidadePorHa),
  );

  return {
    melhor: sorted[sorted.length - 1],
    menor: sorted[0],
  };
}

export function formatTalhaoRendimentoTitulo(talhao) {
  if (!talhao) return '—';
  if (talhao.numero) {
    return `${talhao.numero} — ${talhao.nome}`;
  }
  return talhao.nome;
}

export function formatTalhaoRendimentoRodape(talhao) {
  if (!talhao) return null;

  const cachos =
    talhao.quantidade != null ? `${Number(talhao.quantidade)} cachos` : null;
  const area =
    talhao.areaHa != null ? `${toPrice(talhao.areaHa, 1, 1)} ha` : null;

  return [cachos, area].filter(Boolean).join(' · ') || null;
}

export function supportsMarcacaoCachos(safra) {
  return safra?.product?.gloProdutoId === PRODUTO_BANANA_ID;
}

export const HOME_COLHEITA_SHORTCUT_PATH = '/home/colheita';

export function isHomeColheitaShortcutPath(routePath) {
  const normalized = String(routePath || '').replace(/\/$/, '');

  return (
    normalized === HOME_COLHEITA_SHORTCUT_PATH ||
    normalized.endsWith(HOME_COLHEITA_SHORTCUT_PATH)
  );
}

export function getShortcutDisplayForSafra(routePath, routeItem, safra) {
  if (supportsMarcacaoCachos(safra) && isHomeColheitaShortcutPath(routePath)) {
    return {
      title: 'Emissão',
      description: 'Acompanhe a emissão de cachos',
    };
  }

  return {
    title: routeItem?.title || routePath,
    description: routeItem?.description || '',
  };
}

export function isProdutoBanana(produto) {
  if (!produto) return false;
  if (produto.gloProdutoId === PRODUTO_BANANA_ID) return true;
  return String(produto.descricao || '').toUpperCase() === 'BANANA';
}

export function getTipoEmissaoCachosValue(tipoEmissaoField) {
  return tipoEmissaoField?.id || 'semanal';
}

export function getTipoEmissaoCachosOption(tipoEmissao) {
  return (
    OPTIONS_TIPO_EMISSAO_CACHOS.find((item) => item.id === tipoEmissao) ||
    OPTIONS_TIPO_EMISSAO_CACHOS[0]
  );
}

export function shouldIncludeTipoEmissaoCachosOnSafraSave(
  tipoAtual,
  tipoNovo,
  { isUpdate = false } = {},
) {
  if (!isUpdate) return true;

  const next = getTipoEmissaoCachosValue(tipoNovo);

  if (tipoAtual == null || tipoAtual === '') {
    return next !== 'semanal';
  }

  return next !== tipoAtual;
}

export function appendTipoEmissaoCachosToSafraPayload(
  payload,
  formData,
  { tipoEmissaoAtual, isUpdate = false } = {},
) {
  if (!isProdutoBanana(formData?.produto)) return payload;

  if (
    !shouldIncludeTipoEmissaoCachosOnSafraSave(
      tipoEmissaoAtual,
      formData?.tipo_emissao_cachos,
      { isUpdate },
    )
  ) {
    return payload;
  }

  return {
    ...payload,
    tipo_emissao_cachos: getTipoEmissaoCachosValue(
      formData?.tipo_emissao_cachos,
    ),
  };
}

export function getTalhaoSubtitulo(talhao) {
  if (!talhao) return '';
  return (
    talhao.fazendaNome ||
    talhao.fazenda?.nome ||
    talhao.fazenda ||
    talhao.setor ||
    ''
  );
}

export const MARCACAO_CODIGO_EMISSAO_CONFLITO_MSG =
  'O código de emissão já foi utilizado na safra em outro ano. Escolha outra data.';

export function normalizeMarcacaoListContent(response) {
  if (Array.isArray(response)) return response;
  return response?.content || [];
}

export function compareCodigoEmissao(a, b) {
  const [mesA, emissaoA] = String(a || '')
    .split('/')
    .map((part) => Number(part));
  const [mesB, emissaoB] = String(b || '')
    .split('/')
    .map((part) => Number(part));

  return (
    (Number.isNaN(mesA) ? 0 : mesA) - (Number.isNaN(mesB) ? 0 : mesB) ||
    (Number.isNaN(emissaoA) ? 0 : emissaoA) -
      (Number.isNaN(emissaoB) ? 0 : emissaoB) ||
    String(a || '').localeCompare(String(b || ''))
  );
}

export function buildMarcacaoResumoPivotFilter(safraId, filters, fazendaAtiva) {
  return {
    safra: safraId,
    ...getMarcacaoFazendaFilter(fazendaAtiva),
    ...getMarcacaoScopeFilters(filters),
  };
}

export function buildPivotMarcacaoPorTalhaoEmissao(marcacoes) {
  const codigoFirstDate = new Map();
  const talhaoMap = new Map();

  (marcacoes || []).forEach((item) => {
    const codigo = item?.codigoEmissao;
    if (!codigo) return;

    const dataValue = item?.data ? moment(item.data).valueOf() : null;
    if (
      !codigoFirstDate.has(codigo) ||
      (dataValue != null && dataValue < codigoFirstDate.get(codigo))
    ) {
      codigoFirstDate.set(
        codigo,
        dataValue != null ? dataValue : Number.MAX_SAFE_INTEGER,
      );
    }

    const talhaoId = item.talhao?.id ?? item.talhaoId ?? 'sem-talhao';
    const key = String(talhaoId);

    if (!talhaoMap.has(key)) {
      talhaoMap.set(key, {
        id: key,
        talhaoId,
        numero: formatTalhaoCodigo(item.talhao),
        nome: formatTalhaoNome(item.talhao),
        valores: {},
        total: 0,
      });
    }

    const row = talhaoMap.get(key);
    const quantidade = Number(item.quantidade || 0);
    row.valores[codigo] = Number(row.valores[codigo] || 0) + quantidade;
    row.total += quantidade;
  });

  const colunas = Array.from(codigoFirstDate.entries())
    .sort(
      (a, b) =>
        a[1] - b[1] ||
        compareCodigoEmissao(a[0], b[0]) ||
        a[0].localeCompare(b[0]),
    )
    .map(([codigo]) => codigo);

  const linhas = Array.from(talhaoMap.values()).sort(
    (a, b) =>
      Number(b.total || 0) - Number(a.total || 0) ||
      String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR'),
  );

  const porEmissao = colunas.map((codigo) => ({
    codigo,
    marcados: linhas.reduce(
      (acc, row) => acc + Number(row.valores[codigo] || 0),
      0,
    ),
  }));

  const total = porEmissao.reduce(
    (acc, item) => acc + Number(item.marcados || 0),
    0,
  );

  return { colunas, linhas, porEmissao, total };
}

function isNumericSortValue(value) {
  if (value === '' || value == null) return false;
  return !Number.isNaN(Number(value));
}

function compareMixedSortValues(valueA, valueB) {
  if (isNumericSortValue(valueA) && isNumericSortValue(valueB)) {
    return Number(valueA) - Number(valueB);
  }

  return String(valueA || '').localeCompare(String(valueB || ''), 'pt-BR', {
    numeric: true,
    sensitivity: 'base',
  });
}

function getPivotLinhaSortValue(linha, orderBy) {
  if (orderBy === 'numero') return linha.numero;
  if (orderBy === 'nome') return linha.nome;
  if (orderBy === 'total') return Number(linha.total || 0);
  return Number(linha.valores?.[orderBy] || 0);
}

export function sortPivotMarcacaoLinhas(linhas, sort = {}) {
  const orderBy = sort.order_by || 'total';
  const order = sort.order || 'DESC';
  const direction = order === 'ASC' ? 1 : -1;

  return [...(linhas || [])].sort((linhaA, linhaB) => {
    const comparison =
      compareMixedSortValues(
        getPivotLinhaSortValue(linhaA, orderBy),
        getPivotLinhaSortValue(linhaB, orderBy),
      ) * direction;

    if (comparison !== 0) return comparison;

    return String(linhaA.nome || '').localeCompare(
      String(linhaB.nome || ''),
      'pt-BR',
    );
  });
}

export function buildMarcacaoCodigoEmissaoFilter(safra, codigoEmissao) {
  return {
    safra,
    codigoEmissao,
    page: MARCACAO_CACHOS_PAGE,
    pageSize: MARCACAO_CACHOS_PAGE_SIZE,
  };
}

export function isCodigoEmissaoResponseConflito(response) {
  return Boolean(response?.duplicado || response?.conflito);
}

export function hasCodigoEmissaoConflitoAnual(
  marcacoes,
  data,
  { excludeMarcacaoId } = {},
) {
  if (!data || !marcacoes?.length) return false;

  const selectedYear = moment(data).year();
  if (Number.isNaN(selectedYear)) return false;

  return marcacoes.some((item) => {
    if (excludeMarcacaoId != null && item.id === excludeMarcacaoId) {
      return false;
    }
    if (!item?.data) return false;
    return moment(item.data).year() !== selectedYear;
  });
}

export function getCodigoEmissaoConflitoMensagem(
  marcacoes,
  data,
  { excludeMarcacaoId } = {},
) {
  return hasCodigoEmissaoConflitoAnual(marcacoes, data, { excludeMarcacaoId })
    ? MARCACAO_CODIGO_EMISSAO_CONFLITO_MSG
    : null;
}

export function filterTalhoesMarcacao(talhoes, searchTerm) {
  const term = String(searchTerm || '')
    .trim()
    .toLowerCase();

  if (!term) return talhoes;

  return talhoes.filter((talhao) => {
    const searchable = [talhao.nome, talhao.numero, getTalhaoSubtitulo(talhao)]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());

    return searchable.some((value) => value.includes(term));
  });
}

